import * as React from "react"
import type { Kind } from "../lib/client"

/**
 * The `+`, and what it offers.
 *
 * **The list comes from the client**, and it depends on where you are: inside a board the
 * only sensible things are that board's own items, and outside one they would be cards
 * with a state nobody reads. So this component knows nothing about folders, boards or
 * registries — it renders an answer it was given. A client that learns a new kind offers
 * it here without this file changing.
 */
export const New: React.FC<{
  kinds: Kind[]
  /**
   * What this thing could hang off — **across boards**, not just this one. A technical
   * story answers to a feature on the product board, and a picker that only offered the
   * current board's items would make that link impossible to express.
   */
  parents?: Array<{ path: string; title: string; kind: string; where?: string }>
  label?: string
  onCreate: (kind: string, name: string, itemParent: string | null) => Promise<void>
}> = ({ kinds, parents, label = "+", onCreate }) => {
  const [open, setOpen] = React.useState(false)
  const [kind, setKind] = React.useState<Kind | null>(null)
  const [name, setName] = React.useState("")
  const [parent, setParent] = React.useState("")
  const [busy, setBusy] = React.useState(false)
  const [err, setErr] = React.useState("")

  const close = () => { setOpen(false); setKind(null); setName(""); setParent(""); setErr("") }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!kind) return
    setBusy(true); setErr("")
    try {
      await onCreate(kind.id, name, parent || null)
      close()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  if (!open) {
    return (
      <button className="sf-ghost sf-plus" onClick={() => setOpen(true)}
              aria-label="Create something here">{label}</button>
    )
  }

  return (
    <div className="sf-new">
      {!kind ? (
        <>
          <p className="sf-new-head">What are you making?</p>
          {kinds.length ? kinds.map(k => (
            <button key={k.id} className="sf-kind" onClick={() => setKind(k)}>
              <b>{k.title}</b>
              <span>{k.hint}</span>
            </button>
          )) : (
            <p className="sf-quiet">
              Nothing can be created here. Your client may be older than this page.
            </p>
          )}
          <button className="sf-ghost" onClick={close}>cancel</button>
        </>
      ) : (
        <form onSubmit={submit}>
          <p className="sf-new-head">{kind.title}</p>
          <label className="sf-field">
            <span>name</span>
            <input value={name} autoFocus required onChange={e => setName(e.target.value)} />
          </label>
          {!!parents?.length && (
            <label className="sf-field">
              <span>part of</span>
              <select value={parent} onChange={e => setParent(e.target.value)}>
                {/* Optional on purpose: a technical task answering to nothing above it is
                    the normal case, not an omission. */}
                <option value="">nothing above it</option>
                {parents.map(p => (
                  /* The value is the parent's path, not its slug: a slug is only unique
                     inside one board, and the link now crosses boards. */
                  <option key={p.path} value={p.path}>
                    {p.where ? `${p.where} · ` : ""}{p.kind} · {p.title}
                  </option>
                ))}
              </select>
            </label>
          )}
          <div className="sf-side-actions">
            <button className="sf-btn" disabled={busy || !name.trim()}>
              {busy ? "creating…" : "Create"}
            </button>
            <button type="button" className="sf-ghost" onClick={close}>cancel</button>
          </div>
          {err && <p className="sf-err">{err}</p>}
        </form>
      )}
    </div>
  )
}
